import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '../../actions/framework';
import { ChatModel } from '../chat.model';
import { z } from 'zod/v3';
import { ok } from 'neverthrow';
import type { PromptInputMessage } from '@cotera/client/v0/components/ai-elements/prompt-input';

const stopInputSchema = z.object({
  text: z.string().optional(),
  editorState: z
    .object({
      type: z.literal('doc'),
      content: z.array(z.unknown()).optional(),
    })
    .optional(),
  files: z.array(z.record(z.string(), z.unknown())).optional(),
});

type Params = z.infer<typeof stopInputSchema>;

export class StopAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'Stop Generating';
  inputSchema = stopInputSchema;
  icon = 'stop';
  group = 'Chat';
  description = 'Stop the current coco response';

  applicable(context: ApplicableContext): boolean {
    const chatModel = context.targetedModelOfType(ChatModel);

    return (
      chatModel?.chatController?.status === 'streaming' ||
      chatModel?.chatController?.status === 'submitted'
    );
  }

  async execute(
    params: Params,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const chatModel = context.targetedModelOfType(ChatModel)!;

    const controller = chatModel?.chatController;
    if (
      controller === undefined ||
      (controller.status !== 'submitted' && controller.status !== 'streaming')
    ) {
      return ok({});
    }

    await controller.stop();

    const hasContent =
      (params.text?.trim().length ?? 0) > 0 ||
      (params.editorState?.content?.length ?? 0) > 0 ||
      (params.files?.length ?? 0) > 0;
    if (!hasContent) {
      return ok({});
    }

    const message: PromptInputMessage =
      params.editorState !== undefined
        ? {
            type: 'editor-state',
            editorState: params.editorState,
            files: params.files as PromptInputMessage['files'],
          }
        : {
            type: 'text',
            text: params.text ?? '',
            files: params.files as PromptInputMessage['files'],
          };
    await chatModel.sendMessage(message);

    return ok({});
  }
}
